/**
 * Account-currency money formatting for SaaS billing (INR | USD).
 */

export const DEFAULT_BILLING_CURRENCY = "INR";
export const SAAS_MIN_PAYG_USD = 12;

/** Mirrors API geo-billing: non-India signup defaults to USD when currency is unset. */
const COUNTRY_DEFAULT_CURRENCY = Object.freeze({
  IN: "INR",
  US: "USD",
  GB: "USD",
  AE: "USD",
  SG: "USD",
  AU: "USD",
  CA: "USD",
  DE: "USD",
  FR: "USD",
  NL: "USD",
  IE: "USD",
  NZ: "USD",
  ZA: "USD",
  OTHER: "USD",
});

export function normalizeBillingCurrency(currency) {
  const curr = String(currency || DEFAULT_BILLING_CURRENCY).toUpperCase();
  return curr === "USD" ? "USD" : "INR";
}

/**
 * Account billing currency — explicit currency wins; otherwise infer from country.
 * India always uses INR so Razorpay opens domestic checkout (not USD/PLN DCC).
 */
export function resolveBillingCurrency(source = {}) {
  const countryRaw = source?.country;
  const country = countryRaw != null && countryRaw !== ""
    ? String(countryRaw).trim().toUpperCase()
    : null;

  if (country === "IN") {
    return "INR";
  }

  const explicit = source?.currency;
  if (explicit != null && explicit !== "") {
    return normalizeBillingCurrency(explicit);
  }

  const fallbackCountry = country || "IN";
  return normalizeBillingCurrency(
    COUNTRY_DEFAULT_CURRENCY[fallbackCountry] || DEFAULT_BILLING_CURRENCY
  );
}

export function minPaygUnlockForCurrency(currency) {
  return normalizeBillingCurrency(currency) === "USD" ? SAAS_MIN_PAYG_USD : 1000;
}

/**
 * Account-currency PAYG minimum. Never reuse the INR ₹1000 floor as a USD dollar amount.
 */
export function resolveMinPaygUnlock(source = {}) {
  const currency = resolveBillingCurrency(source);
  const catalogMin = minPaygUnlockForCurrency(currency);
  const inrFloor = minPaygUnlockForCurrency("INR");
  const fromUnlock = Number(source?.minPaygUnlock);
  const fromCommitment = Number(source?.minCommitment);
  const fromInr = Number(source?.minPaygUnlockInr);
  if (currency === "USD") {
    const claimed = [fromUnlock, fromCommitment].find(
      (n) => Number.isFinite(n) && n > 0 && n !== inrFloor
    );
    return claimed || catalogMin;
  }
  const usdFloor = minPaygUnlockForCurrency("USD");
  const claimed = [fromUnlock, fromCommitment, fromInr].find(
    (n) => Number.isFinite(n) && n > 0 && n !== usdFloor
  );
  return claimed || catalogMin;
}

/**
 * Checkout/commitment amount safe for account currency — never treat ₹1000 floor as USD.
 */
export function sanitizeCheckoutAmount(amount, currency, minUnlock) {
  const curr = resolveBillingCurrency({ currency });
  const min = Number(minUnlock) > 0
    ? Number(minUnlock)
    : resolveMinPaygUnlock({ currency: curr });
  const inrFloor = minPaygUnlockForCurrency("INR");
  const parsed = Math.round(Number(amount) || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return min;
  }
  if (curr === "USD" && parsed === inrFloor) {
    return min;
  }
  if (parsed < min) {
    return min;
  }
  return parsed;
}

/** Retry checkout amount: USD uses `amount`, never INR `amountInr`. */
export function resolveCheckoutRetryAmount(row, currency) {
  const curr = normalizeBillingCurrency(
    currency || row?.currency || row?.metadata?.currency
  );
  const catalogMin = minPaygUnlockForCurrency(curr);
  if (curr === "USD") {
    const usdAmount = Number(row?.metadata?.amount ?? row?.metadata?.quote?.amount);
    return Number.isFinite(usdAmount) && usdAmount > 0 ? usdAmount : catalogMin;
  }
  const inrAmount = Number(
    row?.metadata?.amountInr
    ?? row?.metadata?.quote?.amountInr
    ?? row?.metadata?.amount
    ?? row?.metadata?.quote?.listInr
  );
  return Number.isFinite(inrAmount) && inrAmount > 0 ? inrAmount : catalogMin;
}

export function checkoutAmountQuery(amount, currency) {
  const curr = normalizeBillingCurrency(currency);
  return curr === "USD" ? `amount=${amount}` : `amountInr=${amount}`;
}

export function formatMoney(amount, currency = DEFAULT_BILLING_CURRENCY) {
  const curr = normalizeBillingCurrency(currency);
  const n = Number(amount);
  const value = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat(curr === "USD" ? "en-US" : "en-IN", {
    style: "currency",
    currency: curr,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatMoneyExact(amount, currency = DEFAULT_BILLING_CURRENCY) {
  return formatMoney(amount, currency);
}

export function creditExplainer({ currency = DEFAULT_BILLING_CURRENCY, usdInrRate } = {}) {
  const curr = normalizeBillingCurrency(currency);
  if (curr !== "USD") {
    return "₹1 = 4 credits";
  }
  const rate = Number(usdInrRate);
  if (Number.isFinite(rate) && rate > 0) {
    const perUsd = Math.round(rate * 4);
    return `$1 = ${perUsd.toLocaleString("en-US")} credits`;
  }
  return "Credits from live USD→INR (₹1 = 4)";
}

export function creditsForCommitment(amount, { currency = DEFAULT_BILLING_CURRENCY, usdInrRate } = {}) {
  const list = Math.round(Number(amount) || 0);
  if (list <= 0) return 0;
  const curr = normalizeBillingCurrency(currency);
  if (curr !== "USD") {
    return list * 4;
  }
  const rate = Number(usdInrRate);
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return Math.round(list * rate * 4);
}
